import { useContext } from "react";
import { flatMap } from "lodash";
import { useFormContext } from "react-hook-form";
import { OBJECT_TYPE, PROPERTY_OBJECT_LABEL } from "common/constants";
import { arrayHasAtLeastOneItem } from "common/utils/arrays";
import { type Attachment, type Attachments } from "common/utils/attachments";
import {
  formValueDecodeObject,
  type FormValueEncoded,
  formValueEncodeObject,
} from "common/utils/customObjects";
import { AuthContext } from "../../../../Authorization/AuthContext";
import { Label, Select } from "../../../../Inputs";
import { type SubmissionsBuilderFormData, useBuilderWatch } from "./form/form";

export const ATTACHMENT_SELECT_LABEL = "Object type";
export const ATTACHMENT_SELECT_NAME = "attachmentSelect";

type AttachmentFormData = Pick<SubmissionsBuilderFormData, "attachments">;

type AttachmentSelectProps = {
  disabled?: boolean;
};

export function AttachmentSelect({ disabled }: AttachmentSelectProps) {
  const {
    formState: { errors },
    setValue,
    trigger,
  } = useFormContext<AttachmentFormData>();

  const { account } = useContext(AuthContext);
  const customMaps = account?.customMaps ?? [];

  const attachableTagsOptions = [
    {
      value: formValueEncodeObject({ type: OBJECT_TYPE.PROPERTY }),
      label: PROPERTY_OBJECT_LABEL,
    },
  ].concat(
    customMaps
      .filter(map => map.isObject)
      .map(map => ({
        label: map.name,
        value: formValueEncodeObject({
          type: OBJECT_TYPE.CUSTOM_MAP_GEOMETRY,
          customMapId: map.id,
        }),
      })),
  );

  const attachments = useBuilderWatch("attachments");

  const attachmentsValue: FormValueEncoded[] = flatMap(
    attachments,
    attachment =>
      attachment.type === OBJECT_TYPE.PROPERTY ?
        [formValueEncodeObject({ type: OBJECT_TYPE.PROPERTY })]
      : attachment.customMapIds.map(customMapId =>
          formValueEncodeObject({
            type: OBJECT_TYPE.CUSTOM_MAP_GEOMETRY,
            customMapId,
          }),
        ),
  );

  return (
    <div>
      <Label text={ATTACHMENT_SELECT_LABEL} htmlFor={ATTACHMENT_SELECT_NAME} />
      <Select
        name={ATTACHMENT_SELECT_NAME}
        options={attachableTagsOptions}
        value={attachmentsValue}
        size="medium"
        required={true}
        isMulti={true}
        disabled={disabled}
        onChange={async value => {
          if (!value) {
            setValue("attachments", []);
            await trigger("attachments");
            return;
          }

          const newAttachments: Attachment[] = [];
          const newCustomMapIds: string[] = [];

          for (const attachment of value) {
            const formValueDecoded = formValueDecodeObject(attachment);
            if (formValueDecoded.type === OBJECT_TYPE.PROPERTY) {
              newAttachments.push({ type: OBJECT_TYPE.PROPERTY });
            } else {
              newCustomMapIds.push(formValueDecoded.customMapId);
            }
          }

          if (arrayHasAtLeastOneItem(newCustomMapIds)) {
            newAttachments.push({
              type: OBJECT_TYPE.CUSTOM_MAP_GEOMETRY,
              customMapIds: newCustomMapIds,
            });
          }

          setValue("attachments", newAttachments as Attachments);
          await trigger("attachments");
        }}
        error={errors.attachments?.message}
      />
    </div>
  );
}
